'use client';
import { useState } from 'react';
import { X, ChevronUp, ChevronDown } from 'react-feather';
import { Judge } from 'iconsax-react';

import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerClose,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  LoadingSpinner,
} from '@shared/ui';
import { useMobile } from '@shared/hooks';

import { TAgreementsResponse } from '../api';
import { useGetAgreements, language } from '../model';

type TGoldInvestmentAgreementsProps = {
  open: boolean;
  onClose: () => void;
  onAccept?: () => void;
  isEnrollFlow?: boolean;
  agreementsData?: TAgreementsResponse;
};

export const GoldInvestmentAgreements = ({
  open,
  onClose,
  onAccept,
  isEnrollFlow = false,
  agreementsData,
}: TGoldInvestmentAgreementsProps) => {
  const isMobile = useMobile();
  const [collapseOpen, setCollapseOpen] = useState(false);

  const {
    agreements: { data, isLoading: isGettingAgreements },
  } = useGetAgreements(open && !agreementsData);

  const agreements = agreementsData ?? data;

  const handleClose = () => {
    setCollapseOpen(false);
    onClose();
  };

  const handleAccept = () => {
    setCollapseOpen(false);
    if (onAccept) onAccept();
    else onClose();
  };

  const Content = () => {
    if (!agreementsData && isGettingAgreements) {
      return (
        <div className='flex items-center justify-center py-6'>
          <LoadingSpinner />
        </div>
      );
    }
    return (
      <div className='flex flex-col gap-4 flex-1 md:flex-7none md:h-[400px] overflow-y-auto py-4 md:px-5'>
        <div
          className='text-[14px] font-normal leading-[28px] tracking-normal whitespace-pre-line'
          dangerouslySetInnerHTML={{ __html: agreements?.special ?? '' }}
        />
        {agreements?.general && (
          <div className='border border-border rounded-lg'>
            <button
              type='button'
              onClick={() => setCollapseOpen((s) => !s)}
              className='flex items-center justify-between w-full px-4 h-[60px]'
            >
              <span className='text-[13px] font-semibold leading-[150%] tracking-normal text-icons'>
                {language.collapseTitle}
              </span>
              {collapseOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {collapseOpen && (
              <div
                className='px-4 py-3 text-[14px] font-normal leading-[28px] tracking-normal whitespace-pre-line'
                dangerouslySetInnerHTML={{ __html: agreements.general }}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(v) => !v && handleClose()}>
        <DrawerContent className='h-full w-full pb-0'>
          <DrawerHeader className='flex justify-between'>
            <DrawerTitle className='flex items-center justify-center gap-1 text-[16px] leading-[150%]'>
              {language.agreementsTitle}
            </DrawerTitle>
            <DrawerClose onClick={handleClose}>
              <X size={24} strokeWidth={1} />
            </DrawerClose>
          </DrawerHeader>
          {Content()}
          <DrawerFooter className='w-full border-t border-border py-4 px-0'>
            <Button
              type='button'
              onClick={handleAccept}
              className='w-full md:w-[320px] body-14-bold h-[60px] gap-2'
              id='agreement_gold_plan'
            >
              {isEnrollFlow ? (
                <>
                  <Judge size={18} className='text-white' />
                  {language.flowSubmit}
                </>
              ) : (
                language.ok
              )}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className='max-w-[800px] w-full'>
        <DialogHeader className='px-0 md:px-5'>
          <DialogTitle className='flex items-center justify-center gap-1 text-[16px] leading-[150%]'>
            {language.agreementsTitle}
          </DialogTitle>
          <DialogClose onClick={handleClose}>
            <X size={24} strokeWidth={1} />
          </DialogClose>
        </DialogHeader>
        {Content()}
        <DialogFooter className='w-full border-t border-border py-4 px-5'>
          <Button
            type='button'
            onClick={handleAccept}
            className='w-full md:w-[320px] body-14-bold h-[60px] gap-2'
            id='agreement_gold_plan'
          >
            {isEnrollFlow ? (
              <>
                <Judge size={18} className='text-white' />
                {language.flowSubmit}
              </>
            ) : (
              language.ok
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
